// Deal form's layout-driven fields, extracted from CreateDealModal. FormFields returns null
// for relationship fields, so a dropped ConditionalFormField child here silently kills a field.
import React, { ComponentType, ReactNode, useMemo } from "react";

import { useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { Company, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { RawFile } from "@mapmycustomers/shared/types/File";
import FormLayout, { LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import { FormItem, NumberField } from "@mapmycustomers/ui";

import ConditionalFormField, {
  ConditionalFormFieldRenderProps,
} from "@app/component/ConditionalFormField";
import FormFields from "@app/component/FormFields";
import CompanyRelationshipField from "@app/component/FormFields/components/Associations/CompanyRelationshipField";
import PersonRelationshipField from "@app/component/FormFields/components/Associations/PersonRelationshipField";
import RelateEntitiesRequestPayload from "@app/component/FormFields/components/Associations/RelateEntitiesRequestPayload";
import DealLossCommentField from "@app/component/FormFields/components/DealLossCommentField";
import DealLossReasonField from "@app/component/FormFields/components/DealLossReasonField";
import FunnelStageField from "@app/component/FormFields/components/FunnelStageField";
import DatePicker from "@app/component/input/DatePicker";
import AssociationsState from "@app/store/associations/AssociationsState";
import ChangeAssociationsPayload from "@app/store/associations/ChangeAssociationsPayload";
import { MAX_DEAL_SCORE } from "@app/util/consts";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";

import File from "./component/File";
import styles from "./CreateDealModal.module.scss";

// Also typed here because CreateDealModal computes this value for the Form's initialValues too.
export type FunnelStageValue = {
  funnelId?: Funnel["id"];
  stageId?: Stage["id"];
};

export interface DealFormFieldsProps {
  associations: AssociationsState;
  dealStageExtra?: ReactNode;
  defaultAddedFields: LayoutSchemaField[];
  fileComponent?: ComponentType<{
    disabled?: boolean;
    onChange?: (uploadedFiles: RawFile[]) => void;
    required?: boolean;
    supportsTags?: boolean;
  }>;
  fixedCompany?: Company;
  fixedPerson?: Person;
  funnelStageDefaultValues: FunnelStageValue;
  layout: FormLayout;
  onAddField?: (schemaField: LayoutSchemaField) => void;
  onChangeAssociatedEntities: (payload: Omit<ChangeAssociationsPayload, "entityType">) => void;
  relateUnrelateEntities: (payload: RelateEntitiesRequestPayload) => void;
  skipLayoutDefaultsFor?: Set<LayoutSchemaField["field"]>;
}

const DealFormFields = ({
  associations,
  dealStageExtra,
  defaultAddedFields,
  fileComponent,
  fixedCompany,
  fixedPerson,
  funnelStageDefaultValues,
  layout,
  onAddField,
  onChangeAssociatedEntities,
  relateUnrelateEntities,
  skipLayoutDefaultsFor,
}: DealFormFieldsProps) => {
  const intl = useIntl();

  const associatedPeople = useMemo(
    () => (associations.associatedPerson ? [associations.associatedPerson] : undefined),
    [associations.associatedPerson]
  );

  const noAssociations = useMemo(
    () => !associations.associatedCompany && !associations.associatedPerson,
    [associations.associatedCompany, associations.associatedPerson]
  );

  return (
    <FormFields
      defaultAddedFields={defaultAddedFields}
      entityType={EntityType.DEAL}
      fileComponent={fileComponent ?? File}
      isCreateForm
      layout={layout}
      onAddField={onAddField}
      skipLayoutDefaultsFor={skipLayoutDefaultsFor}
    >
      <ConditionalFormField feature={FieldFeature.FUNNEL_FIELD}>
        {({ disabled, isCreateForm, required }: ConditionalFormFieldRenderProps) => (
          <>
            <FunnelStageField
              autoSelectFunnel={false}
              defaultFunnelId={funnelStageDefaultValues.funnelId}
              defaultStageId={funnelStageDefaultValues.stageId}
              disabled={disabled}
              isCreateForm={isCreateForm}
              required={required}
            />
            {dealStageExtra}
          </>
        )}
      </ConditionalFormField>

      <ConditionalFormField fieldName={DealFieldName.DEAL_LOSS_REASON}>
        <DealLossReasonField />
      </ConditionalFormField>

      <ConditionalFormField fieldName={DealFieldName.DEAL_LOSS_COMMENT}>
        <DealLossCommentField />
      </ConditionalFormField>

      <ConditionalFormField fieldName={DealFieldName.SCORE}>
        {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
          <FormItem
            label={label}
            name={DealFieldName.SCORE}
            required={required}
            rules={[{ required }]}
            showLabel={false}
          >
            <NumberField
              disabled={disabled}
              inputClassName={styles.full}
              label={label}
              max={MAX_DEAL_SCORE}
              min={0}
              placeholder={intl.formatMessage(
                {
                  id: "createDealModal.field.score.placeholder",
                  defaultMessage: "0-{max}",
                  description: "Score field's placeholder on the Create Deal modal",
                },
                { max: MAX_DEAL_SCORE }
              )}
              precision={0}
              required={required}
            />
          </FormItem>
        )}
      </ConditionalFormField>

      <ConditionalFormField fieldName={DealFieldName.CLOSING_DATE}>
        {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
          <FormItem
            label={label}
            name={DealFieldName.CLOSING_DATE}
            required={required}
            rules={[{ required }]}
          >
            <DatePicker
              allowClear
              className={styles.full}
              disabled={disabled}
              format={"PP"}
              size="large"
            />
          </FormItem>
        )}
      </ConditionalFormField>

      <ConditionalFormField fieldName={DealFieldName.COMPANY}>
        {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
          <CompanyRelationshipField
            allowAdd={!(disabled && (fixedCompany || fixedPerson))}
            allowClear
            associatedCompany={associations.associatedCompany}
            associatedDeal={associations.associatedDeal}
            associatedPerson={associations.associatedPerson}
            disabled={disabled || !!(fixedCompany || fixedPerson)}
            entityType={EntityType.DEAL}
            label={label}
            onChangeAssociatedEntities={onChangeAssociatedEntities}
            relateEntities={relateUnrelateEntities}
            required={required}
            suggestedCompanies={noAssociations ? undefined : associations.availableCompanies}
          />
        )}
      </ConditionalFormField>

      <ConditionalFormField fieldName={DealFieldName.PERSON}>
        {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
          <PersonRelationshipField
            allowAdd={!(disabled && fixedPerson)}
            allowClear
            associatedCompany={associations.associatedCompany}
            associatedDeal={associations.associatedDeal}
            associatedPeople={associatedPeople}
            disabled={disabled || !!fixedPerson}
            label={label}
            onChangeAssociatedEntities={onChangeAssociatedEntities}
            relateEntities={relateUnrelateEntities}
            required={required}
            suggestedPeople={noAssociations ? undefined : associations.availablePeople}
          />
        )}
      </ConditionalFormField>
    </FormFields>
  );
};

export default DealFormFields;
